/**
 * Comprehensive Logging Service
 * Tracks all operations in the chat application for debugging
 */

export enum LogLevel {
  DEBUG = 'DEBUG',
  INFO = 'INFO',
  WARN = 'WARN',
  ERROR = 'ERROR',
}

export interface LogEntry {
  timestamp: string;
  level: LogLevel;
  category: string;
  message: string;
  data?: any;
}

class LoggingService {
  private logs: LogEntry[] = [];
  private maxLogs = 1000; // Keep last 1000 logs
  private enabled = true;

  log(level: LogLevel, category: string, message: string, data?: any) {
    if (!this.enabled) return;

    const entry: LogEntry = {
      timestamp: new Date().toISOString(),
      level,
      category,
      message,
      data: data ? JSON.parse(JSON.stringify(data)) : undefined, // Deep clone to avoid reference issues
    };

    this.logs.push(entry);

    // Keep only last maxLogs entries
    if (this.logs.length > this.maxLogs) {
      this.logs.shift();
    }

    // Also log to console with appropriate method
    const consoleMessage = `[${entry.timestamp}] [${level}] [${category}] ${message}`;
    switch (level) {
      case LogLevel.DEBUG:
        console.debug(consoleMessage, data || '');
        break;
      case LogLevel.INFO:
        console.info(consoleMessage, data || '');
        break;
      case LogLevel.WARN:
        console.warn(consoleMessage, data || '');
        break;
      case LogLevel.ERROR:
        console.error(consoleMessage, data || '');
        break;
    }
  }

  debug(category: string, message: string, data?: any) {
    this.log(LogLevel.DEBUG, category, message, data);
  }

  info(category: string, message: string, data?: any) {
    this.log(LogLevel.INFO, category, message, data);
  }

  warn(category: string, message: string, data?: any) {
    this.log(LogLevel.WARN, category, message, data);
  }

  error(category: string, message: string, data?: any) {
    this.log(LogLevel.ERROR, category, message, data);
  }

  // Get all logs
  getAllLogs(): LogEntry[] {
    return [...this.logs];
  }

  // Get logs by category
  getLogsByCategory(category: string): LogEntry[] {
    return this.logs.filter(log => log.category === category);
  }

  // Get logs by level
  getLogsByLevel(level: LogLevel): LogEntry[] {
    return this.logs.filter(log => log.level === level);
  }

  // Clear logs
  clearLogs() {
    this.logs = [];
  }

  // Export logs as JSON
  exportLogs(): string {
    return JSON.stringify(this.logs, null, 2);
  }

  // Download logs as file
  downloadLogs(filename: string = `chat-logs-${new Date().toISOString()}.json`) {
    const json = this.exportLogs();
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  // Get state snapshot
  getStateSnapshot(state: any, stateName: string) {
    this.debug('STATE', `State snapshot: ${stateName}`, {
      stateName,
      state: JSON.parse(JSON.stringify(state)), // Deep clone
      timestamp: new Date().toISOString(),
    });
  }

  // Track function execution
  trackFunction(functionName: string, args?: any, result?: any) {
    this.debug('FUNCTION', `Function: ${functionName}`, {
      functionName,
      args: args ? JSON.parse(JSON.stringify(args)) : undefined,
      result: result ? JSON.parse(JSON.stringify(result)) : undefined,
    });
  }

  // Track state changes
  trackStateChange(stateName: string, oldValue: any, newValue: any) {
    this.debug('STATE_CHANGE', `State changed: ${stateName}`, {
      stateName,
      oldValue: oldValue ? JSON.parse(JSON.stringify(oldValue)) : null,
      newValue: newValue ? JSON.parse(JSON.stringify(newValue)) : null,
    });
  }

  // Track API calls
  trackApiCall(method: string, url: string, request?: any, response?: any, error?: any) {
    if (error) {
      this.error('API', `API Call: ${method} ${url}`, {
        method,
        url,
        request: request ? JSON.parse(JSON.stringify(request)) : undefined,
        error: error ? JSON.parse(JSON.stringify(error)) : undefined,
      });
    } else {
      this.info('API', `API Call: ${method} ${url}`, {
        method,
        url,
        request: request ? JSON.parse(JSON.stringify(request)) : undefined,
        response: response ? JSON.parse(JSON.stringify(response)) : undefined,
      });
    }
  }

  // Track message operations
  trackMessageOperation(operation: string, messageId: number, details?: any) {
    this.debug('MESSAGE', `Message operation: ${operation}`, {
      operation,
      messageId,
      ...details,
    });
  }

  // Track rendering
  trackRender(component: string, props?: any, state?: any) {
    this.debug('RENDER', `Rendering: ${component}`, {
      component,
      props: props ? JSON.parse(JSON.stringify(props)) : undefined,
      state: state ? JSON.parse(JSON.stringify(state)) : undefined,
    });
  }
}

// Export singleton instance
export const loggingService = new LoggingService();

// Make it available globally for debugging
if (typeof window !== 'undefined') {
  (window as any).loggingService = loggingService;
  (window as any).getChatLogs = () => loggingService.getAllLogs();
  (window as any).exportChatLogs = () => loggingService.exportLogs();
  (window as any).downloadChatLogs = () => loggingService.downloadLogs();
}

